import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { useMemo } from "react"
import { api } from "../../../../convex/_generated/api"
import type { Doc, Id } from "../../../../convex/_generated/dataModel"
import { useWildGuessSoloEnabled } from "@/lib/analytics/posthog"

export const Route = createFileRoute("/_authed/host/games/$gameId/stats")({
  component: GameStatsPage,
})

function GameStatsPage() {
  const { gameId } = Route.useParams()
  const soloEnabled = useWildGuessSoloEnabled()
  const data = useQuery(api.gameStats.forGame, {
    gameId: gameId as Id<"games">,
  })

  const stats = useMemo(() => {
    if (!data) return null
    const { teams, scores, submissions } = data
    const liveTeams = teams.filter((t) => !t.isDeleted)

    // Per-team running total
    const totalByTeam = new Map<Id<"participants">, number>()
    for (const t of liveTeams) totalByTeam.set(t._id, 0)
    for (const s of scores) {
      totalByTeam.set(
        s.participantId,
        (totalByTeam.get(s.participantId) ?? 0) + s.points,
      )
    }

    // Per-round-per-team cell: decade + number + score (LC: each team
    // picks their own decade per round, so all three are per-cell now)
    const cell = (
      roundId: Id<"rounds">,
      teamId: Id<"participants">,
    ): {
      decade: string | null
      pick: number | null
      score: number | null
    } => {
      const sub = submissions.find(
        (s) => s.roundId === roundId && s.participantId === teamId,
      )
      const payload = sub?.payload as
        | { decade?: string; number?: number }
        | null
      const score = scores.find(
        (s) => s.roundId === roundId && s.participantId === teamId,
      )
      return {
        decade: payload?.decade ?? null,
        pick: payload?.number ?? null,
        score: score?.points ?? null,
      }
    }

    const ranked = liveTeams
      .map((t) => ({ team: t, total: totalByTeam.get(t._id) ?? 0 }))
      .sort((a, b) => b.total - a.total)

    return { ranked, cell }
  }, [data])

  if (data === undefined) {
    return (
      <div className="space-y-4">
        <div className="h-3 w-24 animate-pulse rounded bg-[color:var(--muted)]" />
        <div className="h-7 w-32 animate-pulse rounded bg-[color:var(--muted)]" />
        <div className="h-48 w-full animate-pulse rounded-md bg-[color:var(--muted)]" />
      </div>
    )
  }
  if (data === null) {
    return (
      <div className="rounded-md border border-dashed border-[color:var(--border)] bg-[color:var(--muted)]/30 p-8 text-center">
        <p className="text-sm font-medium">Game not found</p>
        <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
          The link may be wrong, or this game has been removed.
        </p>
      </div>
    )
  }
  const { game, rounds } = data
  if (game.gameType === "solo-trivia" && !soloEnabled) {
    return (
      <div className="rounded-md border border-dashed border-[color:var(--border)] bg-[color:var(--muted)]/30 p-8 text-center">
        <p className="text-sm font-medium">Feature hidden</p>
        <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
          Wild Guess Solo stats are behind a feature flag while host mode stays
          focused on Lyrically Challenged.
        </p>
        <Link
          to="/host"
          className="mt-4 inline-flex items-center gap-1 rounded-md border border-[color:var(--border)] px-3 py-1.5 text-xs hover:bg-[color:var(--muted)]"
        >
          ← Back to host
        </Link>
      </div>
    )
  }
  if (!stats) return null

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/host/games/$gameId"
          params={{ gameId: game._id }}
          className="rounded-sm text-xs text-[color:var(--muted-foreground)] transition-colors hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          ← Back to game console
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">Stats</h1>
        <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
          Status: {game.status} · {rounds.length} round
          {rounds.length === 1 ? "" : "s"} · {stats.ranked.length} team
          {stats.ranked.length === 1 ? "" : "s"}
        </p>
      </div>

      {rounds.length === 0 || stats.ranked.length === 0 ? (
        <div className="rounded-md border border-dashed border-[color:var(--border)] bg-[color:var(--muted)]/30 p-8 text-center">
          <p className="text-sm font-medium">Nothing to show yet</p>
          <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
            Stats will fill in as teams join and rounds wrap up.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-[color:var(--border)] bg-[color:var(--background)]">
          <table className="min-w-full text-sm">
            <thead className="bg-[color:var(--muted)] text-[10px] uppercase tracking-wide text-[color:var(--muted-foreground)]">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Team</th>
                {rounds.map((r) => (
                  <RoundHeader key={r._id} round={r} />
                ))}
                <th className="px-3 py-2 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {stats.ranked.map(({ team, total }, i) => {
                const isLeader = i === 0 && total > 0
                return (
                  <tr
                    key={team._id}
                    className={`border-t border-[color:var(--border)] transition-colors duration-150 ${
                      isLeader
                        ? "bg-amber-50/60"
                        : i % 2 === 1
                          ? "bg-[color:var(--muted)]/30"
                          : ""
                    } hover:bg-[color:var(--muted)]/60`}
                  >
                    <td className="px-3 py-2.5 font-medium">
                      <span className="inline-flex items-center gap-2">
                        {isLeader ? (
                          <MedalIcon />
                        ) : (
                          <span className="inline-block w-4 text-right font-mono text-xs text-[color:var(--muted-foreground)]">
                            {i + 1}
                          </span>
                        )}
                        <span className="truncate">{team.teamName ?? "—"}</span>
                      </span>
                    </td>
                    {rounds.map((r) => {
                      const { decade, pick, score } = stats.cell(r._id, team._id)
                      return (
                        <td
                          key={r._id}
                          className="px-3 py-2.5 text-center align-middle"
                        >
                          <Cell decade={decade} pick={pick} score={score} />
                        </td>
                      )
                    })}
                    <td className="px-3 py-2.5 text-right">
                      <span
                        className={`font-mono tabular-nums ${
                          isLeader
                            ? "text-base font-bold text-amber-900"
                            : "text-sm font-semibold"
                        }`}
                      >
                        {total}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-[color:var(--muted-foreground)]">
        Each cell shows the team's decade, picked number, and points for that
        round. Empty cells mean no submission. Top row is the current leader.
      </p>
    </div>
  )
}

function Cell({
  decade,
  pick,
  score,
}: {
  decade: string | null
  pick: number | null
  score: number | null
}) {
  if (decade === null) {
    return (
      <span className="font-mono text-xs text-[color:var(--muted-foreground)]/60">
        —
      </span>
    )
  }
  return (
    <span className="inline-flex items-baseline gap-1.5 font-mono">
      <span className="text-[10px] uppercase tracking-wider text-[color:var(--muted-foreground)]">
        {decade}
      </span>
      {pick !== null && (
        <>
          <span className="text-sm font-bold tabular-nums">#{pick}</span>
          <span className="text-[11px] tabular-nums text-[color:var(--muted-foreground)]">
            {score ?? "—"}
          </span>
        </>
      )}
    </span>
  )
}

function MedalIcon() {
  return (
    <span
      className="inline-flex h-4 w-4 items-center justify-center"
      title="Current leader"
      aria-label="Current leader"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-amber-500"
      >
        <circle cx="12" cy="14" r="6" fill="currentColor" stroke="none" />
        <path
          d="M8 4l2 5M16 4l-2 5"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    </span>
  )
}

function RoundHeader({ round }: { round: Doc<"rounds"> }) {
  return (
    <th className="px-3 py-2 text-center font-semibold">
      <div>R{round.index + 1}</div>
    </th>
  )
}
